import { fn, hash } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import {
  PrismBadge,
  PrismBulletGraph,
  PrismCard,
  PrismIcon,
  PrismInput,
  PrismMessage,
} from '@mylighthouse/prism-ember';
import EmberTooltip from '@mylighthouse/prism-ember/components/ember-tooltip';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { eq } from 'ember-truth-helpers';
import Fuse from 'fuse.js';
import LoadingBar from 'lumen/components/general/loading-bar';
import GeneralTooltipsTooltipBulletChart from 'lumen/components/general/tooltips/tooltip-bullet-chart';
import type { ParityStrategyBreakdown } from 'lumen/models';
import outsideClickHandler from 'lumen/modifiers/outside-click-handler';
import { DIMENSIONS } from 'lumen/utils/constants/pi-constant-values';
import { timeout } from 'lumen/utils/waiters';

interface ParityStrategyBreakdownBlockLoadingSignature {
  Args: {
    isLoading: true;
  };
}

interface ParityStrategyBreakdownBlockNotLoadingSignature {
  Args: {
    addFilter: (item: ParityStrategyBreakdown) => void;
    breakdownData: ParityStrategyBreakdown[];
    breakdownId: string;
    breakdownTitle: string;
    isLoading?: false;
    isSearchEnabled?: boolean;
    isSelected: (breakdown: ParityStrategyBreakdown) => boolean;
    maxReferenceValue: number;
  };
}

type ParityStrategyBreakdownBlockSignature =
  | ParityStrategyBreakdownBlockLoadingSignature
  | ParityStrategyBreakdownBlockNotLoadingSignature;

export default class ParityStrategyBreakdownBlock extends Component<ParityStrategyBreakdownBlockSignature> {
  @tracked isSearchActive = false;
  @tracked searchValue = '';
  @tracked tempSearchValue = '';

  fuseOptions = {
    ignoreLocation: true,
    threshold: 0.3,
    keys: ['formattedValueWithCustomValue'],
  };

  updateSearchValueTask = task(
    { restartable: true },
    async (event: Event): Promise<void> => {
      this.tempSearchValue = (event.target as HTMLInputElement).value;
      await timeout();
      this.setSearchValue(this.tempSearchValue);
    },
  );

  get filteredBreakdownData(): ParityStrategyBreakdown[] {
    if (this.args.isLoading) {
      return [];
    }

    if (this.searchValue === '') {
      // Return all values if search is empty
      return this.args.breakdownData;
    }

    const fuse = new Fuse(this.args.breakdownData, this.fuseOptions);

    const result = fuse.search(this.searchValue);
    return result.map((item) => item.item);
  }

  get isSearchEnabled(): boolean | undefined {
    if (this.args.isLoading) {
      return false;
    }

    return this.args.isSearchEnabled && this.args.breakdownData.length > 0;
  }

  @action addFilter(item: ParityStrategyBreakdown): void {
    if (this.args.isLoading) {
      return;
    }

    this.args.addFilter(item);
  }

  @action clearSearch(): void {
    this.toggleSearch(false);
    this.tempSearchValue = '';
    this.setSearchValue('');
  }

  @action onInput(event: Event): void {
    void this.updateSearchValueTask.perform(event);
  }

  @action setSearchValue(searchValue: string): void {
    if (this.args.isLoading) {
      return;
    }

    this.searchValue = searchValue;
  }

  @action toggleSearch(isEnabled: boolean): void {
    this.isSearchActive = isEnabled;
  }

  <template>
    {{#if @isLoading}}
      <PrismCard @contentLayout="noPadding">
        <:header>
          <div class="prism-card-title--loading flex items-center">
            <LoadingBar />
          </div>
        </:header>
        <:content>
          <div class="bullet-chart-block flex flex-col pb-200" />
        </:content>
      </PrismCard>
    {{else}}
      <PrismCard @contentLayout="noPadding">
        <:header as |Header|>
          <div class="flex items-center">
            {{#if this.isSearchActive}}
              <div class="flex flex-grow">
                <PrismInput
                  @autofocus={{true}}
                  @iconAfter={{hash name="search"}}
                  @onInput={{this.onInput}}
                  @placeholder={{t "general.search_placeholder"}}
                  @removePadding={{true}}
                  @value={{this.tempSearchValue}}
                  {{outsideClickHandler this.clearSearch}}
                />
              </div>
            {{else}}
              <Header.title>
                <div class="justify-left flex items-center gap-100">
                  {{@breakdownTitle}}
                  {{#if (eq @breakdownId DIMENSIONS.POS)}}
                    <PrismBadge
                      @label={{t "general.coming_soon"}}
                      @type="brand"
                    />
                  {{/if}}
                </div>
              </Header.title>
            {{/if}}
          </div>
        </:header>

        <:headerAfter>
          <div class="flex items-center gap-200">
            {{#if this.isSearchActive}}
              <button
                class="button--unstyled"
                type="button"
                {{on "click" this.clearSearch}}
              >
                <PrismIcon @name="cross" />
              </button>
            {{else}}
              {{#if this.isSearchEnabled}}
                <button
                  class="button--unstyled"
                  data-event-id="market-parity-insight-breakdown-expand"
                  data-event-value={{@breakdownId}}
                  type="button"
                  {{on "click" (fn this.toggleSearch true)}}
                >
                  <PrismIcon @name="search" />
                </button>
              {{/if}}
            {{/if}}
          </div>
        </:headerAfter>

        <:content>
          <div class="bullet-chart-block pb-200">
            {{#if (eq @breakdownData.length 0)}}
              {{#if (eq @breakdownId DIMENSIONS.POS)}}
                <div
                  class="breakdown-list__empty-state-container flex items-center justify-center"
                >
                  <PrismMessage
                    @description={{t "parity.coming_soon.pos"}}
                    @illustration="100x100_binoculars"
                  />
                </div>
              {{else}}
                <div
                  class="breakdown-list__empty-state-container flex items-center justify-center"
                >
                  <PrismMessage
                    @illustration="100x100_critical"
                    @title={{t "general.no_data"}}
                  />
                </div>
              {{/if}}
            {{else if (eq this.filteredBreakdownData.length 0)}}
              <div
                class="breakdown-list__empty-state-container flex items-center justify-center"
              >
                <PrismMessage
                  @illustration="100x100_critical"
                  @title={{t "general.no_results"}}
                />
              </div>
            {{else}}
              {{#each this.filteredBreakdownData as |breakdownItem|}}
                <button
                  class="button--unstyled breakdown-item
                    {{if
                      (@isSelected breakdownItem)
                      'breakdown-item--selected'
                    }}"
                  type="button"
                  {{on "click" (fn this.addFilter breakdownItem)}}
                >
                  <PrismBulletGraph
                    @comparison={{breakdownItem.referenceLossScoreAsInteger}}
                    @current={{breakdownItem.lossScoreAsInteger}}
                    @invertDifferenceColor={{true}}
                    @label={{breakdownItem.formattedValueWithCustomValue}}
                    @max={{@maxReferenceValue}}
                    @symbol="%"
                  />
                  <EmberTooltip
                    @side="top"
                    @tooltipClass="base-tooltip parity-analytics-segmentation-value-tooltip"
                  >
                    <GeneralTooltipsTooltipBulletChart
                      @data={{breakdownItem.tooltipValues}}
                      @invertDifferenceColor={{true}}
                      @title={{breakdownItem.formattedValueWithCustomValue}}
                    />
                  </EmberTooltip>
                </button>
              {{/each}}
            {{/if}}
          </div>
        </:content>
      </PrismCard>
    {{/if}}
  </template>
}
